import React, { useEffect, useState } from 'react';
import { Button } from '@rsos/base-components';
import { useDebugPanelConfig } from '../config';
import { getTcapiCredentials, seedTcapiCredential } from '../services/els';
import {
  StyledCredentialsSection,
  StyledDescription,
  StyledError,
  StyledSuccess,
  StyledWarning,
} from './TextTo911DebugPanel.styles';

type CredentialStatus =
  | 'loading'
  | 'no-credentials'
  | 'has-credentials'
  | 'seeding'
  | 'seeded'
  | 'error';

/**
 * Checks whether TCAPI credentials exist for the current authority and offers
 * a one-click button to seed a mock credential if none are found. The
 * text_to_911 service polls for credentials every 30 seconds, so once seeded
 * it will automatically connect to Shanghai's mock WebSocket server.
 */
export function TcapiCredentialSetup() {
  const { accountId } = useDebugPanelConfig();
  const [credentialStatus, setCredentialStatus] =
    useState<CredentialStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    getTcapiCredentials(accountId)
      .then(credentials => {
        setCredentialStatus(
          credentials.length > 0 ? 'has-credentials' : 'no-credentials',
        );
      })
      .catch(() => {
        setCredentialStatus('no-credentials');
      });
  }, [accountId]);

  async function handleSeed() {
    setCredentialStatus('seeding');
    setErrorMessage('');

    try {
      await seedTcapiCredential(accountId);
      setCredentialStatus('seeded');
    } catch (err) {
      setCredentialStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to seed credential',
      );
    }
  }

  if (credentialStatus === 'loading') {
    return (
      <StyledCredentialsSection>
        <StyledDescription>Checking TCAPI credentials...</StyledDescription>
      </StyledCredentialsSection>
    );
  }

  if (credentialStatus === 'has-credentials' || credentialStatus === 'seeded') {
    return (
      <StyledCredentialsSection>
        <StyledSuccess>
          TCAPI credentials are configured for this authority. The text_to_911
          service should connect to Shanghai&apos;s mock WebSocket server within
          a few seconds.
        </StyledSuccess>
      </StyledCredentialsSection>
    );
  }

  return (
    <StyledCredentialsSection>
      <StyledWarning>
        No TCAPI credentials found for this authority. You must seed a mock
        credential before the text_to_911 service can connect to Shanghai.
      </StyledWarning>
      <Button
        onClick={handleSeed}
        btnType="secondary"
        disabled={credentialStatus === 'seeding'}
      >
        <strong>
          {credentialStatus === 'seeding'
            ? 'Seeding...'
            : 'Seed Mock TCAPI Credential'}
        </strong>
      </Button>
      {credentialStatus === 'error' && (
        <StyledError>
          Error: {errorMessage}
          {errorMessage.includes('403') && (
            <>
              <br />
              <br />
              Credential management requires andromeda permissions on an
              internal org. Make sure you are logged in as a user with access to
              the &quot;RapidSOS Admin&quot; org (e.g. admin+user@rapidsos.com
              on DevBox).
            </>
          )}
        </StyledError>
      )}
    </StyledCredentialsSection>
  );
}
